import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  IconButton,
  TextField,
  Paper,
  Divider,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UpdateUserStyles from "../update-user/UpdateUser.module.css";
import ComponentLoader from "../../components/ComponentLoader";
import { useAppDispatch, useAppSelector } from "../../redux/Hooks";
import { UserSliceActions } from "../../redux/reducers/user-slice";
import { usersDetailResponse } from "../../models/user.model";
import { convertToArray, handleErrorMsg } from "../../components/utils";
import { UserThunkApis } from "../../redux/actions/user-actions";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import {
  APPROVE_USER,
  DELETE_USER_DEFINED_PRACTICE_NAME,
  MAPPING_EXISTING_PRACTICE,
  MAPPING_SINGLE_PRACTICE,
} from "../../constants/api-constants";
import {
  DELETE_SINGLE_MAPPED_PRACTICE_MSG,
  DELETE_USER_DEFINED_PRACTICE_MSG,
  DOCTOR_ROLE,
  DOCTOR_STAFF_ROLE,
  httpStatusCode,
  SINGLE_APPROVED_SUCCESS_MSG,
  USER_APPROVED_FAILED_MSG,
  USER_APPROVED_SUCCESS_MSG,
  USER_DEFINED_PRACTICE_NAME,
  USER_ROLES,
} from "../../constants/variable-constants";
import { ResponseModel } from "../../models/patient.model";
import { ToastAlert } from "../../components/toast-alert";
import { LoadingButton } from "@mui/lab";
import React from "react";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import UpdatePractice from "../update-practice/UpdatePractice";
import { PracticeThunkApis } from "../../redux/actions/practice-actions";
import { practiceUpdatedPayloadProps } from "../../models/practice.model";

const UserApproval = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userId } = useParams<{ userId: string }>();
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const {
    userDetails,
    userDetailsLoading,
    approvedUserId,
    searchTerm,
    userListId,
    userDefinedPractice,
  } = useAppSelector((state) => state.users! || {});
  const updatingValues: usersDetailResponse = {
    ...userDetails!,
    practiceNameId: userDetails
      ? convertToArray(userDetails?.practiceNameId as any)
      : [],
  };
  const { roleId } = useAppSelector((state) => state.auth.userDetails! || {});
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [isRejecting, setIsRejecting] = useState<boolean>(false);
  const [pendingDeleteData, setPendingDeleteData] = useState<{
    approvedUserId: any;
    practiceNames: string[];
    index: number;
  } | null>(null);
  const [pendingPractice, setPendingPractice] = useState<any>(null);

  const { 
  doctorPractices, 
  doctorStaffPractices, 
  adminPractices, 
  practiceLists: practiceListsValues,
} = useAppSelector((state) => state.practices! || {});

let practiceLists: any[] = [];

switch (roleId) {
    case DOCTOR_ROLE:
      practiceLists = doctorPractices;
      break;
    case DOCTOR_STAFF_ROLE:
      practiceLists = doctorStaffPractices;
      break;
    case USER_ROLES.USER_ADMIN:
      practiceLists = adminPractices;
      break;
    default:
      practiceLists = practiceListsValues;
      break;
  }

  const [selectedPractice, setSelectedPractice] = useState<any>({});

  useEffect(() => {
    if (userId) {
      dispatch(UserSliceActions.patchState({ approvedUserId: Number(userId) }));
    } 
    if(practiceLists.length == 0) {
      switch (roleId) {
        case DOCTOR_ROLE:
            dispatch(PracticeThunkApis.getAllDoctorPractices(''));
            break;
        case DOCTOR_STAFF_ROLE:
            dispatch(PracticeThunkApis.getAllDoctorStaffPractices(''));
            break;
        case USER_ROLES.USER_ADMIN:
            dispatch(PracticeThunkApis.getAllAdminPractices(''));
            break;
        default:
            dispatch(PracticeThunkApis.getAllPractices(""));
      }
    }
  }, []);

  const handleDeleteClick = (
    approvedUserId: any,
    practiceNames: string[],
    index: number
  ) => {
    setPendingDeleteData({ approvedUserId, practiceNames, index });
    setIsConfirmationDialogOpen(true);
  };

  const handleApproveClick = async (
    approvedUserId: any,
    practiceNames: string,
    index: any,
    practicePayload?: practiceUpdatedPayloadProps | null
  ) => {
    try {
      if (approvedUserId == null || practiceNames.length === 0) {
        throw new Error("Invalid User ID or Practice Names");
      }
      let data = [];
      if(practicePayload?.practiceName != null) {
        data = [
                {
                  ...practicePayload,
                  userId: approvedUserId
                }
               ];
      } else {
        const practice = practiceLists.find(
            (p) => p.PracticeName === index
          );
        data = [{
            practiceName: practiceNames,
            userId: approvedUserId,
            ...(practice?.PracticeNameId && { practiceNameId: practice.PracticeNameId })
          }]
      }
      const response = await api.fetchAuthPostWithBody<{
        auth: boolean;
        message: string;
      }>(MAPPING_SINGLE_PRACTICE, data);
      if (response?.status === httpStatusCode.SUCCESS) {
        setSelectedPractice((prev: any) => {
          const updatedSelectedPractice = { ...prev };
          delete updatedSelectedPractice[practiceNames[0]];
          return updatedSelectedPractice;
        });
        ToastAlert(SINGLE_APPROVED_SUCCESS_MSG, "success");
        setExpandedRow(null);
        if(roleId === USER_ROLES.USER_ADMIN) {
            dispatch(PracticeThunkApis.getAllAdminPractices(''));
        } else {
            dispatch(PracticeThunkApis.getAllPractices(''));
        }
        dispatch(
          UserThunkApis.getUserDefinedPracticeName({ userId: +approvedUserId })
        );
        // fetch user detail
        dispatch(UserThunkApis.getUserDetails({ userId: approvedUserId }));
      } else {
        throw new Error(DELETE_SINGLE_MAPPED_PRACTICE_MSG);
      }
    } catch (error) {
      handleErrorMsg(error);
    }
  };

  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const toggleAccordion = (index: any) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  useEffect(() => {
    if (practiceLists.length > 0 && pendingPractice != null) {
      const defaultPractice = practiceLists.find(
        (p) => p.PracticeName === pendingPractice.value.PracticeName
      );
      setSelectedPractice((prev: any) => ({
        ...prev,
        [pendingPractice.name]:
          defaultPractice?.PracticeName || pendingPractice.value.PracticeName,
      }));
      setPendingPractice(null);
    }
  }, [practiceLists]);

  const closeAccordion = (practicePayload: practiceUpdatedPayloadProps) => {
   handleApproveClick(approvedUserId, practicePayload.practiceName ? practicePayload.practiceName : "", 0, practicePayload);
  };

  const handleNavigate = () => {
    const assignedList = {
      approved: false,
      waiting: true,
      rejected: false,
    };
    navigate("/home/user_lists");
    dispatch(UserSliceActions.setInitialState());
    dispatch(
      UserSliceActions.patchState({
        activeUserList: assignedList,
        userListId: 0,
        selectedUserMails: [],
      })
    );
    dispatch(UserSliceActions.patchState({ isMenuOpen: false }));
  };

  const handleMapPracticeNames = async () => {
    const data = Object.entries(selectedPractice || {}).map(
      ([practiceName, practiceNameId]) => {
        const practice = practiceLists.find(
          (p) => p.PracticeName === practiceNameId
        );
        return {
          practiceName: practiceName,
          practiceNameId: practice?.PracticeNameId,
          userId: approvedUserId,
        };
      }
    );
    try {
      const response = await api.fetchAuthPostWithBody<{
        auth: boolean;
        message: string;
      }>(MAPPING_EXISTING_PRACTICE, data);
      if (response?.status === httpStatusCode.SUCCESS) {
        await handleApprovedUser();
      }
    } catch (error: any) {
      handleErrorMsg(error);
    }
  };

  const searchParams = {
    query: searchTerm ? searchTerm : "",
    pagesize: null,
    page: null,
    t: userListId,
  };
  const handleApprovedUser = async () => {
    setIsApproving(true);
    try {
      const approveRes = await api.fetchAuthPostWithBody<
        ResponseModel<usersDetailResponse>
      >(APPROVE_USER(approvedUserId!), {});
      if (approveRes?.status === httpStatusCode.SUCCESS) {
        ToastAlert(USER_APPROVED_SUCCESS_MSG, "success");
        setSelectedPractice([]);
        dispatch(UserSliceActions.patchState({ approvedUserId: null }));
        dispatch(
          UserThunkApis.getUserLists({
            userBody: searchParams,
            roleId: roleId!,
          })
        );
        if(approvedUserId != null) {
          dispatch(
            UserThunkApis.getUserDefinedPracticeName({ userId: +approvedUserId })
          );
          dispatch(UserThunkApis.getUserDetails({ userId: approvedUserId.toString() }));
        }
      } else {
        throw new Error(USER_APPROVED_FAILED_MSG);
      }
    } catch (error: any) {
      handleErrorMsg(error);
    } finally {
      setIsApproving(false);
    }
  };

  useEffect(() => {
    if (!approvedUserId) {
      return;
    }
    dispatch(
      UserThunkApis.getUserDefinedPracticeName({ userId: +approvedUserId })
    );
  }, [approvedUserId]);

  const handlePracticeChange = (practiceName: string, value: any | null) => {
    setSelectedPractice((prev: any) => ({
      ...prev,
      [practiceName]: value ? value.PracticeName : null,
    }));
  };

  const handleDeletePractice = async (
    approvedUserId: any,
    practiceNames: string[],
    index: number
  ) => {
    try {
      if (approvedUserId == null || practiceNames.length === 0) {
        throw new Error("Invalid User ID or Practice Names");
      }
      const response = await api.deleteAuthWithBody<{
        auth: boolean;
        message: string;
      }>(DELETE_USER_DEFINED_PRACTICE_NAME(approvedUserId), {
        approvedUserId,
        practiceNames,
      });
      if (response?.status === httpStatusCode.SUCCESS) {
        setSelectedPractice((prev: any) => {
          const updatedSelectedPractice = { ...prev };
          delete updatedSelectedPractice[practiceNames[0]];
          return updatedSelectedPractice;
        });
        ToastAlert(USER_DEFINED_PRACTICE_NAME.DELETED, "success");
        dispatch(
          UserThunkApis.getUserDefinedPracticeName({ userId: +approvedUserId })
        );
      } else {
        throw new Error(DELETE_USER_DEFINED_PRACTICE_MSG);
      }
    } catch (error) {
      handleErrorMsg(error);
    }
  };

  return (
    userDefinedPractice?.length ? <>
    <Box className={UpdateUserStyles.container}>
      <Box className={UpdateUserStyles.search_panal}>
        {userDetailsLoading ? (
          <ComponentLoader />
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 3 }}>
              {/* Title */}
              <Typography variant="h6" gutterBottom>
                Practice Mapping
              </Typography>
              <Typography variant="body2" gutterBottom>
                Map to Existing Practice / Add as a New Practice
              </Typography>
              {/* Practice Mapping */}
              <Box mt={4}>
                <Typography variant="subtitle2" mb={2}>
                  
                </Typography>

                {/* Header row */}
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 2fr 2fr 1fr 1fr"
                  fontWeight="bold"
                  mb={1}
                  px={1}
                >
                  <Typography display="flex" justifyContent="center">
                    Expand
                  </Typography>
                  <Typography>Given Practice Name(s)</Typography>
                  <Typography>Map with existing Practice</Typography>
                  <Typography display="flex" justifyContent="center">
                    Approve
                  </Typography>
                  <Typography display="flex" justifyContent="center">
                    Reject
                  </Typography>
                </Box>
                <Divider />

                {/* Mapping rows */}
                {userDefinedPractice?.map((practice, index) => (
                  <React.Fragment key={index}>
                    <Box
                      key={index}
                      display="grid"
                      gridTemplateColumns="1fr 2fr 2fr 1fr 1fr"
                      alignItems="center"
                      gap={2}
                      my={1}
                      px={1}
                    >
                      {/* Expand */}
                      <Box display="flex" justifyContent="center">
                        <IconButton
                          color="primary"
                          onClick={() => toggleAccordion(index)}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      {/* Given Practice Name */}
                      <Box
                        key={index}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mt={2}
                        mb={3}
                      >
                        <Box
                          flex="1"
                          ml="20px"
                          mr={3}
                          p={1.5}
                          sx={{
                            border: "1px solid #e0e0e0",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "4px",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: "black",
                              fontSize: "1.0rem",
                            }}
                          >
                            {practice}
                          </Typography>
                        </Box>
                      </Box>
                      {/* Select Practice */}
                      <Box
                        flex="1"
                        sx={{ textAlign: "center", paddingX: "10px" }}
                      >
                        <FormControl
                          fullWidth
                          sx={{ backgroundColor: "white" }}
                        >
                          <Autocomplete
                            key={practice}
                            options={practiceLists || []}
                            getOptionLabel={(option: any) =>
                              option.PracticeName || ""
                            }
                            value={
                              selectedPractice[practice]
                                ? practiceLists.find(
                                    (practiceOption) =>
                                      practiceOption.PracticeName ===
                                      selectedPractice[practice]
                                  ) || null
                                : null
                            }
                            onChange={(event, value) =>
                              handlePracticeChange(practice, value)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Practice Name"
                                variant="outlined"
                                size="small"
                                sx={{
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#6fc1e8",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6fc1e8",
                                    },
                                }}
                              />
                            )}
                            freeSolo
                          />
                        </FormControl>
                      </Box>

                      {/* Add */}
                      <Box display="flex" justifyContent="center">
                        <IconButton
                          color="success"
                          onClick={() =>
                            handleApproveClick(approvedUserId, practice, selectedPractice[practice], null)
                          }
                        >
                          <CheckIcon />
                        </IconButton>
                      </Box>

                      {/* Reject */}
                      <Box display="flex" justifyContent="center">
                        <IconButton
                          color="error"
                          onClick={() =>
                            handleDeleteClick(approvedUserId, [practice], index)
                          }
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* Accordion Content (Add New Practice Form) */}
                    {expandedRow === index && (
                      <>
                        <UpdatePractice
                          closeAccordion={(practiseName) =>
                            closeAccordion(practiseName)
                          }
                          isUserApproval={true}
                          selectedPracticeName={practice}
                        />
                      </>
                    )}
                  </React.Fragment>
                ))}
              </Box>
              {/* END */}
            </Paper>
          </>
        )}
      </Box>
      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        accept={() => {
          if (pendingDeleteData) {
            const { approvedUserId, practiceNames, index } = pendingDeleteData;
            handleDeletePractice(approvedUserId, practiceNames, index);
          }
          setIsConfirmationDialogOpen(false);
        }}
        reject={() => {
          setIsConfirmationDialogOpen(false);
        }}
        msg="Are you sure you want to reject the User-Defined practice? Once rejected, it cannot be reverted."
        acceptText={"Yes"}
        rejectText={"No"}
        isLoading={isRejecting}
      />
    </Box>
    </> : null
  );
};

export default UserApproval;
